import React, { useMemo, useRef, useState } from "react";
import type { SceneMeta } from "../../api";
import { ScenePreviewPopup } from "./ScenePreviewPopup";
import { getReleaseBadge, formatReleaseDate, formatRelativeAge } from "../../lib/release-badge";

type Props = {
  scenes: SceneMeta[];
  onAdd: (scene: SceneMeta) => void;
};

type SortMode = "newest" | "oldest" | "default";

/** Effektives "zuletzt bearbeitet"-Datum: updatedAt bevorzugt, sonst createdAt. */
function effectiveDate(s: SceneMeta): string | undefined {
  return s.updatedAt ?? s.createdAt;
}

function effectiveTs(s: SceneMeta): number {
  const iso = effectiveDate(s);
  if (!iso) return 0;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? 0 : t;
}

const CATEGORIES: Array<SceneMeta["category"] | "all"> = [
  "all",
  "intro",
  "text",
  "data",
  "list",
  "visual",
  "partner",
  "outro",
];

const CATEGORY_LABEL: Record<string, string> = {
  all: "Alle",
  intro: "Intro",
  text: "Text / Hook",
  data: "Zahlen / Stats",
  list: "Listen / Steps",
  visual: "Visual / Mockup",
  partner: "Partner",
  outro: "Outro / CTA",
};

const LOOKS: Array<string | "all"> = ["all", "kinetic", "minimal", "punchy", "data", "mockup", "social"];

const LOOK_LABEL: Record<string, string> = {
  all: "Alle Stile",
  kinetic: "Kinetic",
  minimal: "Minimal",
  punchy: "Punchy",
  data: "Data",
  mockup: "Mockup",
  social: "Social",
};

const CATEGORY_EMOJI: Record<string, string> = {
  intro: "🎬",
  text: "✏️",
  data: "📊",
  list: "📋",
  visual: "🖼️",
  partner: "🤝",
  outro: "👋",
};

const LIB_THEME_KEY = "jusa.library.theme";

export const SceneLibrary: React.FC<Props> = ({ scenes, onAdd }) => {
  const [filter, setFilter] = useState<SceneMeta["category"] | "all">("all");
  const [look, setLook] = useState<string>("all");
  const [q, setQ] = useState("");
  const [sort, setSort] = useState<SortMode>("newest");
  const [onlyNew, setOnlyNew] = useState<boolean>(false);
  const [libTheme, setLibTheme] = useState<"dark" | "light">(() => {
    if (typeof window === "undefined") return "dark";
    return window.localStorage.getItem(LIB_THEME_KEY) === "light" ? "light" : "dark";
  });
  const [hover, setHover] = useState<{ scene: SceneMeta; rect: DOMRect } | null>(null);
  const hoverTimerRef = useRef<number | null>(null);

  const toggleLibTheme = (): void => {
    setLibTheme((prev) => {
      const next = prev === "dark" ? "light" : "dark";
      if (typeof window !== "undefined") window.localStorage.setItem(LIB_THEME_KEY, next);
      return next;
    });
  };

  const armHover = (scene: SceneMeta, target: HTMLElement): void => {
    if (hoverTimerRef.current) window.clearTimeout(hoverTimerRef.current);
    hoverTimerRef.current = window.setTimeout(() => {
      setHover({ scene, rect: target.getBoundingClientRect() });
    }, 280);
  };

  const cancelHover = (): void => {
    if (hoverTimerRef.current) {
      window.clearTimeout(hoverTimerRef.current);
      hoverTimerRef.current = null;
    }
    setHover(null);
  };

  const visible = useMemo(() => {
    const list = scenes.filter((s) => {
      if (filter !== "all" && s.category !== filter) return false;
      if (look !== "all" && s.look !== look) return false;
      if (q.trim()) {
        const t = q.toLowerCase();
        const hay = `${s.label} ${s.description} ${s.id}`.toLowerCase();
        if (!hay.includes(t)) return false;
      }
      if (onlyNew && !getReleaseBadge(s).isNew) return false;
      return true;
    });
    if (sort === "newest") {
      return [...list].sort((a, b) => effectiveTs(b) - effectiveTs(a));
    }
    if (sort === "oldest") {
      return [...list].sort((a, b) => effectiveTs(a) - effectiveTs(b));
    }
    return list;
  }, [scenes, filter, look, q, sort, onlyNew]);

  const reset = (): void => {
    setFilter("all");
    setLook("all");
    setQ("");
    setOnlyNew(false);
  };

  const isFiltered = filter !== "all" || look !== "all" || q.trim().length > 0 || onlyNew;

  return (
    <div className="scene-library">
      <div className="scene-library-head">
        <div className="library-search-wrap">
          <input
            className="input"
            placeholder="🔎 Scene suchen…"
            value={q}
            onChange={(e) => setQ(e.target.value)}
          />
          {isFiltered && (
            <button className="library-reset" onClick={reset} title="Filter zurücksetzen">
              ✕
            </button>
          )}
        </div>
        <div className="chip-row">
          {CATEGORIES.map((c) => (
            <button
              key={c}
              className={`chip ${filter === c ? "active" : ""}`}
              onClick={() => setFilter(c)}
            >
              {c !== "all" && <span className="chip-emoji">{CATEGORY_EMOJI[c]}</span>}
              {CATEGORY_LABEL[c]}
            </button>
          ))}
        </div>
        <div className="chip-row chip-row-look">
          {LOOKS.map((l) => (
            <button
              key={l}
              className={`chip chip-look ${look === l ? "active" : ""}`}
              data-look={l !== "all" ? l : undefined}
              onClick={() => setLook(l)}
            >
              {LOOK_LABEL[l]}
            </button>
          ))}
        </div>
        <div className="chip-row" style={{ gap: 6 }}>
          <button
            className={`chip ${sort === "newest" ? "active" : ""}`}
            onClick={() => setSort("newest")}
            title="Zuletzt bearbeitet zuerst"
          >
            📅 Neueste
          </button>
          <button
            className={`chip ${sort === "oldest" ? "active" : ""}`}
            onClick={() => setSort("oldest")}
            title="Älteste zuerst"
          >
            🕰 Älteste
          </button>
          <button
            className={`chip ${sort === "default" ? "active" : ""}`}
            onClick={() => setSort("default")}
            title="Reihenfolge aus der Registry"
          >
            Standard
          </button>
          <button
            className={`chip ${onlyNew ? "active" : ""}`}
            onClick={() => setOnlyNew((v) => !v)}
            title="Nur Scenes aus den letzten 14 Tagen"
          >
            ✦ Nur NEU
          </button>
          <button
            className={`chip ${libTheme === "light" ? "active" : ""}`}
            onClick={toggleLibTheme}
            title="Vorschau-Theme der Library umschalten (Dark/Light) — gilt global für alle Scene-Vorschauen"
            style={{ marginLeft: "auto" }}
          >
            {libTheme === "light" ? "☀️ Light" : "🌙 Dark"}
          </button>
        </div>
        <div className="library-count">
          {visible.length} / {scenes.length} Scenes
        </div>
      </div>

      <div className="scene-library-list">
        {visible.map((s) => {
          const badge = getReleaseBadge(s);
          const editedIso = effectiveDate(s);
          const editedDate = formatReleaseDate(editedIso);
          const editedRel = formatRelativeAge(editedIso);
          return (
          <button
            key={s.id}
            className="scene-card"
            onClick={() => onAdd(s)}
            title={
              editedDate
                ? `Klicken: einfügen · Hover: Vorschau · Zuletzt bearbeitet ${editedDate}${editedRel ? ` (${editedRel})` : ""}`
                : "Klicken: einfügen · Hover: Vorschau"
            }
            onMouseEnter={(e) => armHover(s, e.currentTarget)}
            onMouseLeave={cancelHover}
            onFocus={(e) => armHover(s, e.currentTarget)}
            onBlur={cancelHover}
          >
            {badge.isNew && (
              <div className="release-stamp release-stamp-corner">
                <span className="release-pill-new">NEU</span>
              </div>
            )}
            <div className="scene-card-head">
              <span className="scene-card-cat">{CATEGORY_EMOJI[s.category]}</span>
              <span className="scene-card-title">{s.label}</span>
              <span className="scene-card-look" data-look={s.look}>
                {LOOK_LABEL[s.look] ?? s.look}
              </span>
            </div>
            <div className="scene-card-desc">{s.description}</div>
            <div className="scene-card-meta">
              <span>{s.defaultDurationSec.toFixed(1)}s</span>
              {editedDate && (
                <span className="release-date" title={editedRel ? `zuletzt bearbeitet · ${editedRel}` : undefined}>
                  ✎ {editedDate}
                </span>
              )}
              <span className="scene-card-preview-hint">👁 Vorschau</span>
              <span className="scene-card-add">+ einfügen</span>
            </div>
          </button>
          );
        })}
        {visible.length === 0 && (
          <div className="empty">
            Keine Scenes gefunden.
            <br />
            <button className="btn ghost" style={{ marginTop: 8 }} onClick={reset}>
              Filter zurücksetzen
            </button>
          </div>
        )}
      </div>

      {hover && (
        <ScenePreviewPopup
          scene={hover.scene}
          theme={libTheme}
          anchor={{
            top: hover.rect.top,
            left: hover.rect.left,
            width: hover.rect.width,
            height: hover.rect.height,
          }}
        />
      )}
    </div>
  );
};
